import React, { useState } from "react";
import { useSpring, animated } from "@react-spring/web";
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Typography,
  Tooltip,
  Button,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FlashcardImageUpload from "./FlashcardImageUpload";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { MathJaxContext, MathJax } from "better-react-mathjax";

import CircularProgress from "@mui/material/CircularProgress";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { callApi } from "../api";
import { trackLesson, escapeLatexInJson, fixLatexNotation } from "../functions";

import { useTheme } from "@mui/material/styles";

import { getStyles } from "../../index";

// Create animated version of MUI Paper
const AnimatedPaper = animated(Paper);

const FlashcardItem = ({
  flashcard,
  index,
  handleFrontChange,
  handleBackChange,
  handleDeleteCard,
  handleTabPress,
  isDark,
  styles,
  user,
  showSnackbar,
  onImageUpdate,
}) => {
  const [frontPreviewMode, setFrontPreviewMode] = useState(!!flashcard.front);
  const [backPreviewMode, setBackPreviewMode] = useState(!!flashcard.back);

  const theme = useTheme();

  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [regeneratePrompt, setRegeneratePrompt] = useState("");
  const [isRegenerating, setIsRegenerating] = useState(false);

  // Add these handler functions to your FlashcardItem component
  const handleOpenRegenerateDialog = () => {
    setRegenerateDialogOpen(true);
  };

  const handleCloseRegenerateDialog = () => {
    setRegenerateDialogOpen(false);
    setRegeneratePrompt("");
  };

  // Function to handle regenerating the flashcard
  const handleRegenerateFlashcard = async () => {
    if (!regeneratePrompt.trim()) {
      return; // Don't proceed if prompt is empty
    }

    setIsRegenerating(true);
    setRegenerateDialogOpen(false);

    try {
      // Prepare the message for the API
      const messages = [
        {
          role: "system",
          content: `You are an expert at improving educational flashcards. You will be given a flashcard JSON object and instructions on how to improve it. Modify the flashcard following these instructions, but maintain the same JSON structure. Always respond with valid JSON only.


Om det är matematik-tema: När du använder LaTeX-syntax MÅSTE du omsluta VARJE matematiskt uttryck (oavsett om det är enkla operationer eller komplexa ekvationer) med dollartecken på följande sätt:

För uttryck inom textrader: $x \times y$
För fristående uttryck: $$x \times y$$

Använd ALDRIG [], () eller andra tecken runt matematiska uttryck. ALLTID dollartecken.




          `,
        },
        {
          role: "user",
          content: `Here is a flashcard in JSON format:\n\n${JSON.stringify(flashcard, null, 2)}\n\nPlease modify this flashcard based on the following instruction: ${regeneratePrompt}. Return only the improved JSON object with the same structure.`,
        },
      ];

      // Call the API
      const response = await callApi(messages, 2000, true, user.uid);

      // Extract the response content
      const messageContent = response.data.choices[0].message.content.trim();

      // Parse the JSON from the response
      let parsedResponse;
      try {
        // Extract JSON if it's wrapped in code blocks
        let jsonContent = messageContent;
        if (jsonContent.startsWith("```json")) {
          jsonContent = jsonContent.slice(7, -3).trim();
        } else if (jsonContent.startsWith("```")) {
          jsonContent = jsonContent.slice(3, -3).trim();
        }
        jsonContent = escapeLatexInJson(jsonContent);
        parsedResponse = JSON.parse(jsonContent);

        // Update the flashcard
        const updatedFlashcard = {
          ...flashcard,
          front: parsedResponse.front,
          back: parsedResponse.back,
        };

        handleFrontChange(index, { target: { value: updatedFlashcard.front } });
        handleBackChange(index, { target: { value: updatedFlashcard.back } });

        await trackLesson(
          user.uid,
          "FlashcardViewRegenerateCard.js",
          null,
          null,
        );

        showSnackbar("Flashcard har uppdaterats!", "success");
      } catch (parseError) {
        console.error("Error parsing JSON response:", parseError);
        showSnackbar("Något gick fel. Försök igen.", "error");
      }
    } catch (error) {
      console.error("Error regenerating flashcard:", error);
      showSnackbar("Något gick fel. Försök igen.", "error");
    } finally {
      setIsRegenerating(false);
    }
  };

  const springProps = useSpring({
    from: {
      opacity: 0,
      transform: "translateY(20px)",
    },
    to: {
      opacity: 1,
      transform: "translateY(0px)",
    },
    delay: index * 20,
    config: {
      mass: 1,
      tension: 280,
      friction: 20,
    },
  });

  const toggleFrontPreviewMode = () => {
    setFrontPreviewMode(!frontPreviewMode);
  };

  const toggleBackPreviewMode = () => {
    setBackPreviewMode(!backPreviewMode);
  };

  // Modify the existing conditional to handle both isGenerating and isRegenerating
  if (flashcard.isGenerating || isRegenerating) {
    return (
      <AnimatedPaper
        elevation={2}
        style={springProps}
        sx={{
          position: "relative",
          p: 3,
          pt: 5,
          mb: 3,
          borderRadius: "10px",
          backgroundImage: "none",
          boxShadow: "0 3px 28px rgba(12,8,51,.06)",
          opacity: 0.8,
          border: (theme) => `1px dashed ${theme.palette.primary.main}`,
        }}
        className="flashcard-item"
      >
        {/* Flashcard Number */}
        <Typography
          variant="body2"
          sx={{
            position: "absolute",
            top: 16,
            left: 16,
            color: isDark ? "grey.400" : "grey.600",
            fontWeight: 600,
            fontSize: "1.2rem",
          }}
        >
          {index + 1}.
        </Typography>
        {/* Content */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            width: "100%",
            margin: 0,
            paddingTop: "20px",
          }}
        >
          <Box sx={{ width: "100%", textAlign: "center", py: 4 }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <Box sx={{ position: "relative" }}>
                <CircularProgress size={40} />
              </Box>
            </Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 1, fontSize: "1.1rem" }}
            >
              {isRegenerating
                ? "Förbättrar flashcard..."
                : "Genererar flashcards..."}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: "0.85rem" }}
            >
              Vänligen vänta några sekunder
            </Typography>
          </Box>
        </Box>
      </AnimatedPaper>
    );
  }

  return (
    <>
      <MathJaxContext
        config={{
          tex: {
            inlineMath: [
              ["$", "$"],
              ["\\(", "\\)"],
            ],
            displayMath: [
              ["$$", "$$"],
              ["\\[", "\\]"],
            ],
          },
          chtml: {
            scale: 1.3, // Global scaling factor
            fontFamily: "Arial", // Font to use
            matchFontHeight: false, // Match surrounding font height
            displayAlign: "left", // How to align displayed equations
            displayIndent: "0", // Indent for displayed equations
          },
        }}
      >
        <AnimatedPaper
          elevation={2}
          style={springProps}
          sx={{
            position: "relative",
            p: 3,
            pt: 5,
            mb: 3,
            borderRadius: "10px",
            backgroundImage: "none",
            boxShadow: "0 3px 28px rgba(12,8,51,.06)",
          }}
        >
          {/* Flashcard Number */}
          <Typography
            variant="body2"
            sx={{
              position: "absolute",
              top: 16,
              left: 16,
              color: isDark ? "grey.400" : "grey.600",
              fontWeight: 600,
              fontSize: "1.2rem",
            }}
          >
            {index + 1}.
          </Typography>

          {/* Delete Button */}
          <Tooltip title="Ta bort flashcard" placement="top">
            <IconButton
              onClick={() => handleDeleteCard(index)}
              aria-label="delete"
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
              }}
            >
              <DeleteOutlineIcon sx={{ color: "text.secondary" }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Förbättra med AI" placement="top">
            <IconButton
              onClick={handleOpenRegenerateDialog}
              sx={{ position: "absolute", top: 8, right: 48 }}
            >
              <AutoFixHighIcon
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: 23,
                }}
              />
            </IconButton>
          </Tooltip>

          {/* Content */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              width: "100%",
              margin: 0,
              paddingTop: "20px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "center", md: "flex-start" },
                mb: { xs: 2, md: 0 },
              }}
            >
              <FlashcardImageUpload
                userId={user.uid}
                imageUrl={flashcard.imageUrlFront}
                onImageUpdate={(url) => onImageUpdate(index, url, "front")}
                showSnackbar={showSnackbar}
                styles={styles}
                text="Bild på framsidan"
              />
            </Box>

            <Box sx={{ flex: 1, width: "100%", mb: { xs: 3, md: 0 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: isDark ? "grey.400" : "grey.600",
                    ml: 1,
                    mb: 1,
                    display: "block",
                    fontWeight: "500",
                    fontSize: { xs: "0.9rem", md: "1rem" },
                  }}
                >
                  Framsida
                </Typography>
                <Tooltip
                  title={frontPreviewMode ? "Redigera" : "Förhandsgranska"}
                >
                  <IconButton
                    size="small"
                    onClick={toggleFrontPreviewMode}
                    sx={{ mr: 1 }}
                  >
                    {frontPreviewMode ? (
                      <></>
                    ) : (
                      <VisibilityIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>

              {frontPreviewMode ? (
                <Box sx={{ position: "relative" }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      minHeight: "77px",
                      background: (theme) =>
                        theme.palette.background.paperHover,
                      borderRadius: 4,
                      display: "flex",
                      alignItems: "center",
                      borderColor: isDark ? "grey.700" : "grey.300",
                      transition: "all 0.2s ease",
                      cursor: "pointer",
                      position: "relative",
                      overflow: "hidden",
                      "&:hover": {
                        borderColor: isDark ? "primary.main" : "primary.main",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                      },
                      "&:hover .edit-icon": {
                        opacity: 1,
                        transform: "translateY(0)",
                      },
                    }}
                    onClick={toggleFrontPreviewMode}
                  >
                    <MathJax dynamic>
                      {flashcard.front ? (
                        <span>{fixLatexNotation(flashcard.front)}</span>
                      ) : (
                        <Typography
                          color="text.secondary"
                          sx={{ fontStyle: "italic" }}
                        >
                          Klicka för att redigera
                        </Typography>
                      )}
                    </MathJax>

                    {/* Edit icon - subtle and modern */}
                    <Box
                      className="edit-icon"
                      sx={{
                        position: "absolute",
                        bottom: 8,
                        right: 8,
                        display: "flex",
                        alignItems: "center",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        bgcolor: isDark
                          ? "rgba(66, 66, 66, 0.8)"
                          : "rgba(238, 238, 238, 0.8)",
                        opacity: 0,
                        transform: "translateY(10px)",
                        transition: "all 0.2s ease",
                        backdropFilter: "blur(4px)",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: isDark ? "primary.light" : "primary.main",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          fontWeight: 500,
                          fontSize: "0.75rem",
                        }}
                      >
                        <EditIcon sx={{ fontSize: 14 }} /> Redigera
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              ) : (
                <TextField
                  id={`front-${index}`}
                  placeholder="Kortets framsida"
                  value={flashcard.front}
                  onChange={(e) => handleFrontChange(index, e)}
                  onKeyDown={(e) => handleTabPress(index, false, e)}
                  fullWidth
                  multiline
                  rows={{ xs: 3, md: 2 }}
                  margin="none"
                  sx={{ fontWeight: "600" }}
                />
              )}
            </Box>

            <Box sx={{ flex: 1, width: "100%", mb: { xs: 2, md: 0 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: isDark ? "grey.400" : "grey.600",
                    ml: 1,
                    mb: 1,
                    display: "block",
                    fontWeight: "500",
                    fontSize: { xs: "0.9rem", md: "1rem" },
                  }}
                >
                  Baksida
                </Typography>
                <Tooltip
                  title={backPreviewMode ? "Redigera" : "Förhandsgranska"}
                >
                  <IconButton
                    size="small"
                    onClick={toggleBackPreviewMode}
                    sx={{ mr: 1 }}
                  >
                    {backPreviewMode ? (
                      <></>
                    ) : (
                      <VisibilityIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>

              {backPreviewMode ? (
                <Box sx={{ position: "relative" }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      minHeight: "77px",
                      background: (theme) =>
                        theme.palette.background.paperHover,
                      borderRadius: 4,
                      display: "flex",
                      alignItems: "center",
                      borderColor: isDark ? "grey.700" : "grey.300",
                      transition: "all 0.2s ease",
                      cursor: "pointer",
                      position: "relative",
                      overflow: "hidden",
                      "&:hover": {
                        borderColor: isDark ? "primary.main" : "primary.main",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                      },
                      "&:hover .edit-icon": {
                        opacity: 1,
                        transform: "translateY(0)",
                      },
                    }}
                    onClick={toggleBackPreviewMode}
                  >
                    <MathJax dynamic>
                      {flashcard.back ? (
                        <span>{fixLatexNotation(flashcard.back)}</span>
                      ) : (
                        <Typography
                          color="text.secondary"
                          sx={{ fontStyle: "italic" }}
                        >
                          Klicka för att redigera
                        </Typography>
                      )}
                    </MathJax>

                    {/* Edit icon - subtle and modern */}
                    <Box
                      className="edit-icon"
                      sx={{
                        position: "absolute",
                        bottom: 8,
                        right: 8,
                        display: "flex",
                        alignItems: "center",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        bgcolor: isDark
                          ? "rgba(66, 66, 66, 0.8)"
                          : "rgba(238, 238, 238, 0.8)",
                        opacity: 0,
                        transform: "translateY(10px)",
                        transition: "all 0.2s ease",
                        backdropFilter: "blur(4px)",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: isDark ? "primary.light" : "primary.main",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          fontWeight: 500,
                          fontSize: "0.75rem",
                        }}
                      >
                        <EditIcon sx={{ fontSize: 14 }} /> Redigera
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              ) : (
                <TextField
                  id={`back-${index}`}
                  placeholder="Kortets baksida"
                  value={flashcard.back}
                  onChange={(e) => handleBackChange(index, e)}
                  onKeyDown={(e) => handleTabPress(index, true, e)}
                  fullWidth
                  rows={{ xs: 3, md: 2 }}
                  multiline
                  margin="none"
                  sx={{ fontWeight: "600" }}
                />
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              <FlashcardImageUpload
                userId={user.uid}
                imageUrl={flashcard.imageUrlBack}
                onImageUpdate={(url) => onImageUpdate(index, url, "back")}
                showSnackbar={showSnackbar}
                styles={styles}
                text="Bild på baksidan"
              />
            </Box>
          </Box>
        </AnimatedPaper>
      </MathJaxContext>
      <Dialog
        open={regenerateDialogOpen}
        onClose={handleCloseRegenerateDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Förbättra flashcard med AI
          <IconButton
            aria-label="close"
            onClick={handleCloseRegenerateDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2, mt: 2 }}>
            Beskriv hur du vill förbättra eller ändra flashcardet:
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            placeholder="T.ex. 'Gör det mer pedagogiskt', 'Förenkla språket', 'Lägg till fler detaljer'"
            value={regeneratePrompt}
            onChange={(e) => setRegeneratePrompt(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleRegenerateFlashcard}
            variant="purp"
            disabled={isRegenerating || !regeneratePrompt.trim()}
            startIcon={
              isRegenerating ? (
                <CircularProgress sx={{ color: "white" }} size={16} />
              ) : (
                <AutoFixHighIcon />
              )
            }
          >
            {isRegenerating ? "Förbättrar..." : "Förbättra flashcard"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FlashcardItem;
